import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, PencilIcon } from 'lucide-react';
import { kordisKit } from '../../data/kordisKit';
import { DURATION, EASE_OUT } from '../../utils/motion';
const MANROPE = {
  fontFamily: "'Manrope', 'Poppins', system-ui, sans-serif"
};

/**
 * Live specimens rebuilt from the Chat CFO UI Kit (AIInsight, AIDraftContainer, ThinkingToolCallChat).
 * They sit on a fixed light "product" surface so they look like the real kit in both site themes.
 */
export function KitSpecimens() {
  return <div className="rounded-2xl bg-[#F6F6FB] p-4 md:p-8" style={MANROPE} data-id="element-519" data-mp="2gqfn529n7iqf">
      <div className="grid gap-4 md:grid-cols-2" data-id="element-520" data-mp="2bquj4dfm8k6u">
        <Specimen name="AIInsight" note="Confidence in words, source named" data-id="element-521" data-mp="2bs5naecz3tyh">
          <InsightCard data-id="element-522" data-mp="2ppub9v2c7h7r" />
        </Specimen>
        <Specimen name="AIDraftContainer" note="AI output stays a draft until you edit or accept it" data-id="element-523" data-mp="2pqvoo66tsfsn">
          <DraftCard data-id="element-524" data-mp="2kpg0i59cpt61" />
        </Specimen>
        <Specimen name="ThinkingToolCallChat" note="Shows what the agent is doing, not a spinner" data-id="element-525" data-mp="3spp5rzecsxft">
          <ThinkingRow data-id="element-526" data-mp="1icozlhdum0f" />
        </Specimen>
        <Specimen name="Brand tokens" note="Manrope, flat data colors" data-id="element-527" data-mp="1j4kyxew6lr8">
          <Palette data-id="element-528" data-mp="6gxo1hkwezna" />
        </Specimen>
      </div>
    </div>;
}
function Specimen({
  name,
  note,
  children
}: {
  name: string;
  note: string;
  children: React.ReactNode;
}) {
  return <div className="flex h-full flex-col rounded-xl border border-[#E7E7F2] bg-white" data-id="element-529" data-mp="fj6w80sp2gxi">
      <div className="flex items-baseline justify-between gap-3 border-b border-[#EEEEF5] px-4 py-3" data-id="element-530" data-mp="khgwgpta07pz">
        <p className="text-[13px] font-bold text-black" data-id="element-531" data-mp="p7x8q6ld1nar">{name}</p>
        <p className="truncate text-[11px] text-[#6B7280]" data-id="element-532" data-mp="pajogtien942">{note}</p>
      </div>
      <div className="grid flex-1 place-items-center px-4 py-6 md:px-6" data-id="element-533" data-mp="u70bfkdvbhfk">{children}</div>
    </div>;
}
function InsightCard() {
  const {
    insight
  } = kordisKit;
  return <div className="w-full max-w-sm rounded-2xl border border-[#E5E5FA] bg-[#F4F4FE] p-5" data-id="element-534" data-mp="1399n4dkvhguj">
      <div className="flex items-start gap-3" data-id="element-535" data-mp="3082uzylykenh">
        <span aria-hidden className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-[#6666E9]" data-id="element-536" data-mp="306rr7ysij6wd" />
        <p className="text-base font-bold leading-snug text-black" data-id="element-537" data-mp="2v7oio4dbqijn">{insight.title}</p>
      </div>
      <p className="mt-2 pl-[22px] text-[13px] leading-relaxed text-[#374151]" data-id="element-538" data-mp="392h8pwqubh4h">{insight.description}</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 pl-[22px]" data-id="element-539" data-mp="348cyn4ma2sz6">
        <span className="rounded-full bg-[#D6F5E1] px-2.5 py-0.5 text-[11px] font-semibold text-[#067647]" data-id="element-540" data-mp="3ixivwn1k294f">
          {insight.confidence}
        </span>
        <span className="text-xs font-medium text-[#6B7280]" data-id="element-541" data-mp="3s0bmsp6tsitq">{insight.source}</span>
      </div>
      <div className="ml-[22px] mt-3 border-t border-[#E5E5FA]" data-id="element-542" data-mp="3nxx6abqmpv5m" />
      <p className="mt-2.5 pl-[22px] text-[13px] font-semibold text-[#6666E9]" data-id="element-543" data-mp="3nwm0y7hjcfce">
        {insight.action} <span aria-hidden data-id="element-544" data-mp="1xnu5dy8o4cx0">→</span>
      </p>
    </div>;
}
function DraftCard() {
  const {
    draft
  } = kordisKit;
  const [editing, setEditing] = useState(false);
  return <div className="w-full max-w-sm" data-id="element-545" data-mp="1jh0m4n3wz9zl">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]" data-id="element-546" data-mp="1ni2ld3xtzm1k">Proposed journal entry</p>
      <div className={`relative rounded-xl bg-white px-4 py-3.5 transition-[border-color] duration-200 ease-out ${editing ? 'border-[1.5px] border-[#6666E9]' : 'border border-dashed border-[#D1D5DB]'}`} data-id="element-547" data-mp="19r78lgk7oksp">
        <button type="button" onClick={() => setEditing(v => !v)} aria-pressed={editing} className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-[#6B7280] transition-colors duration-150 hover:bg-[#F9FAFB] hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6666E9]" data-id="element-548" data-mp="zr6o3ralvymx">
          {editing ? <>
              <CheckIcon className="h-3 w-3 text-[#6666E9]" strokeWidth={3} aria-hidden data-id="element-549" data-mp="2q5h0gknnwroa" />
              <span className="text-[#6666E9]" data-id="element-550" data-mp="q2fqzb40e79s">Done</span>
            </> : <>
              <PencilIcon className="h-3 w-3" aria-hidden data-id="element-551" data-mp="l336uy4iveaw" />
              Edit
            </>}
        </button>
        <p className="pr-16 text-[11px] text-[#6B7280]" data-id="element-552" data-mp="g3gt8nks7w1k">
          Date <span className="text-black" data-id="element-553" data-mp="c2ww3k07ksff">{draft.date}</span>
        </p>
        <table className="mt-3 w-full text-[12px]" data-id="element-554" data-mp="c2n6vymzmpas">
          <thead data-id="element-555" data-mp="74bsdalqzqm8">
            <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]" data-id="element-556" data-mp="74vbgun4wl1u">
              <th className="pb-1.5 text-left font-semibold" data-id="element-557" data-mp="2c27wa3ejirc">Account</th>
              <th className="pb-1.5 text-right font-semibold" data-id="element-558" data-mp="3tiv9vg60rlwx">Debit</th>
              <th className="pb-1.5 text-right font-semibold" data-id="element-559" data-mp="3th20vu7xoplr">Credit</th>
            </tr>
          </thead>
          <tbody data-id="element-560" data-mp="3kiqe63a6ubyl">
            {draft.lines.map(line => <tr key={line.account} className="border-t border-[#F1F1F6] text-black" data-id="element-561" data-mp="3ki6vgkcprjji">
                <td className="py-2 pr-2" data-id="element-562" data-mp="3fkybqfbx37g5">{line.account}</td>
                <td className="py-2 text-right tabular-nums" data-id="element-563" data-mp="1v1usu5pvrs5t">
                  <EditableValue value={line.debit} editing={editing} data-id="element-564" data-mp="1v1b86h8uipo3" />
                </td>
                <td className="py-2 pl-2 text-right tabular-nums" data-id="element-565" data-mp="1zyuornt0nej7">
                  <EditableValue value={line.credit} editing={editing} data-id="element-566" data-mp="243c7djnn3lse" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function EditableValue({
  value,
  editing
}: {
  value: string;
  editing: boolean;
}) {
  if (!editing || value === '—') return <>{value}</>;
  return <span className="rounded bg-[#F4F4FE] px-1 py-0.5 text-[#6666E9]" data-id="element-567" data-mp="1gd81f5mzb9y8">{value}</span>;
}
function ThinkingRow() {
  const reduce = useReducedMotion();
  const steps = kordisKit.thinkingSteps;
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setStep(s => (s + 1) % steps.length), 1800);
    return () => window.clearInterval(id);
  }, [reduce, steps.length]);
  return <div className="flex items-center gap-3" aria-live="off" data-id="element-568" data-mp="20wuudc14u7dp">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#EEEEF5] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)]" data-id="element-569" data-mp="20v9ur7zyny8z">
        <img src={kordisKit.logo} alt="" className="h-5 w-5 object-contain" data-id="element-570" data-mp="29poatcsbfct5" />
      </span>
      <span className="relative inline-flex h-10 min-w-[190px] items-center overflow-hidden rounded-xl border border-[#E7E7F2] bg-white px-4 text-[13px] font-medium text-[#6666E9]" data-id="element-571" data-mp="25nb68rpb1kxy">
        <AnimatePresence mode="popLayout" initial={false} data-id="element-572" data-mp="1cz5ma762rzz6">
          <motion.span key={steps[step]} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: DURATION.small,
          ease: EASE_OUT
        }} data-id="element-573" data-mp="1d0qg1ukkfevz">
            {steps[step]}…
          </motion.span>
        </AnimatePresence>
      </span>
    </div>;
}
function Palette() {
  return <ul className="grid w-full max-w-sm grid-cols-2 gap-3" data-id="element-574" data-mp="2xeh8bdol5spv">
      {kordisKit.palette.map(c => <li key={c.hex} className="flex items-center gap-2.5" data-id="element-575" data-mp="35ugcpqraldkb">
          <span aria-hidden className="h-9 w-9 shrink-0 rounded-lg" style={{
        backgroundColor: c.hex
      }} data-id="element-576" data-mp="39xv0x4pdx08u" />
          <span className="min-w-0" data-id="element-577" data-mp="2vyvdu1histes">
            <span className="block text-[12px] font-bold text-black" data-id="element-578" data-mp="2vzoeqltssop0">{c.name}</span>
            <span className="block truncate text-[11px] text-[#6B7280]" data-id="element-579" data-mp="30xgdnzsv1b2p">{c.role}</span>
          </span>
        </li>)}
    </ul>;
}